import { useCallback, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFirestore, collection, getDocs, query, orderBy, limit, where, startAfter } from 'firebase/firestore';
import { PhotoIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, PlusIcon, XMarkIcon, QueueListIcon } from '@heroicons/react/24/outline';
import { auth } from '../firebase';
import CatalogCapacityHeadline from '../catalog/CatalogCapacityHeadline';
import { fetchContentManifest, updateStimulusState } from '../catalog/catalogApi';
import { catalogCapacity } from '../catalog/catalogCapacity';
import { isLabelingIncomplete, isTannerStimulus } from './labelStatus';

export default function ViewStimuli() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stimuli, setStimuli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [capacity, setCapacity] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(true);
  const [capacityError, setCapacityError] = useState('');
  const [togglingHashes, setTogglingHashes] = useState({});
  const [toggleErrors, setToggleErrors] = useState({});
  const [filters, setFilters] = useState({
    gender: null,
    control: null,
    tanner: null
  });
  const itemsPerPage = 10;
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    fetchStimuli();
  }, [filters, currentPage]);

  const loadManifest = useCallback(async () => {
    setCapacityLoading(true);
    setCapacityError('');
    try {
      const token = await auth.currentUser.getIdToken();
      const manifest = await fetchContentManifest({ token });
      setCapacity(catalogCapacity(manifest.images));
    } catch (err) {
      console.error('Error fetching global manifest capacity:', err);
      setCapacityError(err.message || 'Global catalog capacity could not be loaded.');
    } finally {
      setCapacityLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  const fetchStimuli = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      let queryConstraints = [orderBy('hash')];

      // Add filters
      if (filters.control !== null) {
        queryConstraints.push(where('control', '==', filters.control));
      } else if (filters.gender !== null) {
        queryConstraints.push(where('gender', '==', filters.gender));
      }
      if (filters.tanner !== null) {
        queryConstraints.push(where('tanner', '==', filters.tanner));
      }

      // Get total count first
      const countQuery = query(collection(db, 'images'), ...queryConstraints);
      const countSnapshot = await getDocs(countQuery);
      setTotalCount(countSnapshot.size);

      // If we're on page 1, just get the first page
      if (currentPage === 1) {
        const firstPageQuery = query(collection(db, 'images'), ...queryConstraints, limit(itemsPerPage));
        const firstPageSnapshot = await getDocs(firstPageQuery);
        const stimuliData = firstPageSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStimuli(stimuliData);
      } else {
        // For subsequent pages, we need to get the last document of the previous page
        const previousPageQuery = query(collection(db, 'images'), ...queryConstraints, limit((currentPage - 1) * itemsPerPage));
        const previousPageSnapshot = await getDocs(previousPageQuery);
        const lastDoc = previousPageSnapshot.docs[previousPageSnapshot.docs.length - 1];

        if (lastDoc) {
          // Get the current page's documents
          const currentPageQuery = query(
            collection(db, 'images'),
            ...queryConstraints,
            startAfter(lastDoc),
            limit(itemsPerPage)
          );
          const currentPageSnapshot = await getDocs(currentPageQuery);
          const stimuliData = currentPageSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setStimuli(stimuliData);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching stimuli:', err);
      setError('Failed to load stimuli');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (type, value) => {
    setFilters(prev => {
      // If clicking on a gender/control cell
      if (type === 'gender' && typeof value === 'number') {
        return { ...prev, gender: null, control: value, tanner: null };
      } else if (type === 'gender') {
        return { ...prev, gender: value, control: null, tanner: null };
      }
      // If clicking on a tanner cell
      return { ...prev, tanner: value, control: null };
    });
    // Reset to page 1 when filters change
    setSearchParams({ page: '1' });
  };

  const removeFilter = (filterType) => {
    setFilters(prev => ({ ...prev, [filterType]: null }));
    // Reset to page 1 when filters change
    setSearchParams({ page: '1' });
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setSearchParams({ page: (currentPage - 1).toString() });
    }
  };

  const handleNext = () => {
    const maxPage = Math.ceil(totalCount / itemsPerPage);
    if (currentPage < maxPage) {
      setSearchParams({ page: (currentPage + 1).toString() });
    }
  };

  const handleViewStimulus = (stimulus) => {
    navigate(`/stimuli/${stimulus.hash}?page=${currentPage}`);
  };

  const handleAddStimulus = () => {
    navigate('/stimuli/add');
  };

  const handleLabelQueue = () => {
    navigate('/stimuli/queue');
  };

  const toggleStimulus = async (stimulus) => {
    if (togglingHashes[stimulus.hash]) {
      return;
    }
    const enabled = stimulus.enabled === false;
    setTogglingHashes((current) => ({ ...current, [stimulus.hash]: true }));
    setToggleErrors((current) => ({ ...current, [stimulus.hash]: '' }));
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await updateStimulusState({
        token,
        hash: stimulus.hash,
        enabled,
      });
      setStimuli((current) => current.map((item) => (
        item.hash === stimulus.hash ? { ...item, enabled: response.enabled } : item
      )));
      await loadManifest();
    } catch (err) {
      console.error('Failed to update stimulus:', err);
      setToggleErrors((current) => ({
        ...current,
        [stimulus.hash]: err.message || 'Stimulus could not be updated.',
      }));
    } finally {
      setTogglingHashes((current) => {
        const next = { ...current };
        delete next[stimulus.hash];
        return next;
      });
    }
  };

  const truncateHash = (hash) => {
    if (!hash) return 'N/A';
    return `${hash.substring(0, 8)}...${hash.substring(hash.length - 8)}`;
  };

  if (loading && currentPage === 1) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Stimuli</h1>
          <p className="mt-2 text-sm text-gray-700">
            A list of all stimuli in the system with their attributes and controls.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleLabelQueue}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600"
            >
              <QueueListIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Label Queue
            </button>
            <button
              type="button"
              onClick={handleAddStimulus}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Add Stimulus
            </button>
          </div>
        </div>
      </div>

      <CatalogCapacityHeadline
        kind="stimulus"
        capacity={capacity}
        loading={capacityLoading}
        error={capacityError}
      />

      {/* Active Filters */}
      {(filters.gender || filters.control || filters.tanner) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {filters.control && (
            <span className="inline-flex items-center gap-x-0.5 rounded-md bg-purple-50 px-2 py-1 text-sm font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
              Control #{filters.control}
              <button
                type="button"
                onClick={() => removeFilter('control')}
                className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-purple-600/20"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
          {filters.gender && (
            <span className="inline-flex items-center gap-x-0.5 rounded-md bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
              Gender: {filters.gender}
              <button
                type="button"
                onClick={() => removeFilter('gender')}
                className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-blue-600/20"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
          {filters.tanner && (
            <span className="inline-flex items-center gap-x-0.5 rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
              Tanner: {filters.tanner}
              <button
                type="button"
                onClick={() => removeFilter('tanner')}
                className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-green-600/20"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
        </div>
      )}

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Hash
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Gender/Control
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Tanner
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Availability
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {stimuli.map((stimulus) => (
                    <tr key={stimulus.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        <div className="flex items-center">
                          <PhotoIcon className="h-5 w-5 text-gray-400" />
                          <span className="ml-2 font-mono">{truncateHash(stimulus.hash)}</span>
                          {isTannerStimulus(stimulus) && stimulus.labeling_complete === true && (
                            <span className="ml-2 inline-flex items-center rounded-md bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/20">
                              labeling complete (manual)
                            </span>
                          )}
                          {isTannerStimulus(stimulus) && stimulus.labeling_complete !== true && isLabelingIncomplete(stimulus) && (
                            <span className="ml-2 inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20">
                              labeling incomplete
                            </span>
                          )}
                          {isTannerStimulus(stimulus) && stimulus.enabled === false && (
                            <span className="ml-2 inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10">
                              disabled
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {stimulus.control ? (
                          <button
                            onClick={() => handleFilterClick('gender', stimulus.control)}
                            className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-sm font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10 hover:bg-purple-100"
                          >
                            Control #{stimulus.control}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleFilterClick('gender', stimulus.gender)}
                            className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 hover:bg-blue-100"
                          >
                            {stimulus.gender || 'N/A'}
                          </button>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {!stimulus.control && (
                          <button
                            onClick={() => handleFilterClick('tanner', stimulus.tanner)}
                            className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-700/10 hover:bg-green-100"
                          >
                            {stimulus.tanner || 'N/A'}
                          </button>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {isTannerStimulus(stimulus) ? (
                          <div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={stimulus.enabled !== false}
                              aria-label={`${stimulus.enabled === false ? 'Enable' : 'Disable'} stimulus ${stimulus.hash}`}
                              disabled={Boolean(togglingHashes[stimulus.hash])}
                              onClick={() => toggleStimulus(stimulus)}
                              className={`relative inline-flex h-6 w-11 rounded-full border-2 border-transparent transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-40 ${
                                stimulus.enabled !== false ? 'bg-emerald-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                aria-hidden="true"
                                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                                  stimulus.enabled !== false ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                            {toggleErrors[stimulus.hash] && (
                              <p role="alert" className="mt-1 max-w-48 whitespace-normal text-xs font-medium text-red-700">
                                {toggleErrors[stimulus.hash]}
                              </p>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate('/control-slides')}
                            className="text-sm font-semibold text-indigo-600 hover:text-indigo-900"
                          >
                            Manage slide
                          </button>
                        )}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <div className="flex justify-end gap-2">
                        {(stimulus.tanner && (!stimulus.labels || Object.keys(stimulus.labels).length === 0)) &&
                            <button
                              onClick={() => handleViewStimulus(stimulus)}
                              className="text-green-600 hover:text-green-900"
                              title="Add Labels"
                            >
                              <PlusIcon className="h-5 w-5" aria-hidden="true" />
                              <span className="sr-only">Add Labels for {stimulus.hash}</span>
                            </button>
                          }
                          <button
                            onClick={() => handleViewStimulus(stimulus)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <EyeIcon className="h-5 w-5" aria-hidden="true" />
                            <span className="sr-only">View {stimulus.hash}</span>
                          </button>

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalCount)}</span> of{' '}
              <span className="font-medium">{totalCount}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={handleNext}
                disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
} 
